import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  Ticket,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  TrendingUp,
} from "lucide-react";
import { toast } from "react-toastify";
import eventService from "../../../../api/EventService";

const EventAnalytics = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { event_slug } = useParams();
  const [eventData, setEventData] = useState(location.state?.eventData || null);
  const [isLoading, setIsLoading] = useState(!location.state?.eventData);
  const [error, setError] = useState(null);

  const [analytics] = useState({
    totalSales: 25650,
    ticketSalesData: [
      { day: "Mon", sales: 1200 },
      { day: "Tue", sales: 1900 },
      { day: "Wed", sales: 1500 },
      { day: "Thurs", sales: 1800 },
      { day: "Fri", sales: 2100 },
      { day: "Sat", sales: 1950 },
      { day: "Sun", sales: 1400 },
    ],
    attendeeTurnout: {
      scanned: 80,
      unscanned: 20,
      total: 5725,
    },
    ticketTypeDistribution: {
      vip: 80,
      regular: 63,
      premium: 38,
      total: 5725,
    },
    demographics: {
      gender: { male: 45, female: 55 },
      age: {
        "19-25": 30,
        "26-30": 25,
        "<18": 15,
        "31-35": 20,
        "36-40": 10,
      },
      location: {
        "Los Angeles": 35,
        "New York": 25,
        Portland: 15,
        Paris: 15,
        Miami: 10,
      },
    },
  });

  useEffect(() => {
    const fetchEventData = async () => {
      if (location.state?.eventData) {
        setEventData(location.state.eventData);
        setIsLoading(false);
        return;
      }

      if (!event_slug) {
        setError("Event identifier is missing");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const eventResponse = await eventService.getEvent(event_slug);

        if (!eventResponse.status || !eventResponse.data) {
          throw new Error(eventResponse.message || "Failed to fetch event");
        }

        let event = null;
        if (eventResponse.data.status_code === 200 && eventResponse.data.data) {
          event = eventResponse.data.data;
        } else if (eventResponse.data && !eventResponse.data.status_code) {
          event = eventResponse.data;
        }

        if (!event) {
          throw new Error("Event data not found");
        }

        setEventData(event);
      } catch (err) {
        const errorMessage =
          err.message || "An error occurred while loading event data";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEventData();
  }, [event_slug, location.state]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 mx-auto mb-4 text-purple-600 animate-spin" />
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !eventData) {
    return (
      <div className="px-6 py-4">
        <button
          onClick={() => navigate("/admin/media/events")}
          className="flex items-center mb-6 text-gray-600 transition-colors hover:text-gray-900 group"
        >
          <ArrowLeft className="mr-2 w-4 h-4 transition-transform group-hover:-translate-x-1" />
          <span className="text-sm font-medium">Back to Events</span>
        </button>

        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="p-4 mb-4 bg-red-100 rounded-full">
            <AlertCircle className="w-12 h-12 text-red-600" />
          </div>
          <h2 className="mb-2 text-xl font-bold text-gray-900">
            Error Loading Analytics
          </h2>
          <p className="mb-6 text-gray-600">{error || "Event not found"}</p>
          <button
            onClick={() => navigate("/admin/media/events")}
            className="px-6 py-2 text-white bg-purple-600 rounded-lg transition-colors hover:bg-purple-700"
          >
            Go Back to Events
          </button>
        </div>
      </div>
    );
  }

  // Determine where to navigate back based on where user came from
  const handleBack = () => {
    const from = location.state?.from;
    
    if (from === 'events-table') {
      // Navigate back to events management page
      navigate('/admin/media/events');
    } else {
      // Default: navigate back to event details page
      navigate(`/admin/media/events/view/${event_slug}`, {
        state: { eventData: eventData },
      });
    }
  };

  const getBackButtonLabel = () => {
    const from = location.state?.from;
    return from === 'events-table' ? 'Back to Events' : 'Back to Event Details';
  };

  return (
    <div className="px-6 py-4 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between w-full">
        <button
          onClick={handleBack}
          className="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg transition-colors hover:bg-purple-700 cursor-pointer w-full sm:w-auto self-start"
        >
          <ArrowLeft className="mr-2 w-4 h-4 flex-shrink-0" />
          <span className="truncate">{getBackButtonLabel()}</span>
        </button>
        <div className="w-full sm:w-auto">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">
            {eventData.event_title || eventData.venue_name || "Event"} Analytics
          </h1>
        </div>
      </div>

      {/* In-depth Analytics */}
      <div>
        <h2 className="mb-4 text-xl font-bold text-gray-900">
          In-depth Analytics
        </h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Real-time Ticket Sales Tracking */}
          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="mb-1 text-lg font-semibold text-gray-900">
                    Real-time Ticket Sales Tracking
                  </h3>
                  <p className="text-xs text-gray-500">
                    Weekly sales performance
                  </p>
                </div>
                <div className="p-2 bg-orange-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-orange-600" />
                </div>
              </div>
              <div className="flex items-center space-x-6">
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1">
                    Total Sales
                  </p>
                  <p className="text-2xl font-bold text-orange-600">
                    ₵{analytics.totalSales.toLocaleString()}
                  </p>
                </div>
                <div className="h-10 w-px bg-gray-200" />
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1">
                    Average/Day
                  </p>
                  <p className="text-xl font-bold text-orange-700">
                    ₵
                    {Math.round(
                      analytics.totalSales / analytics.ticketSalesData.length
                    ).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
            <TicketSalesChart data={analytics.ticketSalesData} />
          </div>

          {/* Attendee Turnout Distribution */}
          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="mb-6">
              <h3 className="mb-1 text-lg font-semibold text-gray-900">
                Attendee Turnout Distribution
              </h3>
              <p className="text-xs text-gray-500">Check-in status overview</p>
            </div>
            <div className="flex flex-col items-center space-y-6 lg:flex-row lg:items-start lg:space-y-0 lg:space-x-8">
              {/* Enhanced Donut Chart */}
              <div
                className="relative flex-shrink-0"
                style={{ width: "180px", height: "180px" }}
              >
                <svg width="180" height="180" className="transform -rotate-90">
                  {/* Background circle */}
                  <circle
                    cx="90"
                    cy="90"
                    r="70"
                    fill="transparent"
                    stroke="#F3F4F6"
                    strokeWidth="16"
                  />
                  {/* Scanned segment */}
                  <circle
                    cx="90"
                    cy="90"
                    r="70"
                    fill="transparent"
                    stroke="#10B981"
                    strokeWidth="16"
                    strokeDasharray={`${
                      (analytics.attendeeTurnout.scanned / 100) * 439.82
                    } 439.82`}
                    strokeLinecap="round"
                    className="transition-all duration-500"
                  />
                  {/* Unscanned segment */}
                  <circle
                    cx="90"
                    cy="90"
                    r="70"
                    fill="transparent"
                    stroke="#E5E7EB"
                    strokeWidth="16"
                    strokeDasharray={`${
                      (analytics.attendeeTurnout.unscanned / 100) * 439.82
                    } 439.82`}
                    strokeDashoffset={`-${
                      (analytics.attendeeTurnout.scanned / 100) * 439.82
                    }`}
                    strokeLinecap="round"
                    className="transition-all duration-500"
                  />
                </svg>
                {/* Center content */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <p className="text-xs font-medium text-gray-500 mb-1">
                    Total Attendees
                  </p>
                  <p className="text-3xl font-bold text-gray-900">
                    {analytics.attendeeTurnout.total.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {analytics.attendeeTurnout.scanned}% Checked In
                  </p>
                </div>
              </div>

              {/* Statistics */}
              <div className="flex-1 w-full space-y-4">
                <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-green-500 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          Scanned
                        </p>
                        <p className="text-xs text-gray-600">
                          Checked in attendees
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">
                        {analytics.attendeeTurnout.scanned}%
                      </p>
                      <p className="text-xs text-gray-500">
                        {Math.round(
                          (analytics.attendeeTurnout.scanned / 100) *
                            analytics.attendeeTurnout.total
                        ).toLocaleString()}{" "}
                        people
                      </p>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-green-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-500"
                      style={{ width: `${analytics.attendeeTurnout.scanned}%` }}
                    />
                  </div>
                </div>

                <div className="p-4 bg-gradient-to-r from-gray-50 to-slate-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-gray-400 rounded-lg">
                        <XCircle className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          Unscanned
                        </p>
                        <p className="text-xs text-gray-600">
                          Not yet checked in
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-600">
                        {analytics.attendeeTurnout.unscanned}%
                      </p>
                      <p className="text-xs text-gray-500">
                        {Math.round(
                          (analytics.attendeeTurnout.unscanned / 100) *
                            analytics.attendeeTurnout.total
                        ).toLocaleString()}{" "}
                        people
                      </p>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-gray-400 to-gray-500 rounded-full transition-all duration-500"
                      style={{
                        width: `${analytics.attendeeTurnout.unscanned}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Ticket Type Distribution */}
          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="mb-1 text-lg font-semibold text-gray-900">
                    Ticket Type Distribution
                  </h3>
                  <p className="text-xs text-gray-500">
                    Sales by ticket category
                  </p>
                </div>
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Ticket className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center space-y-6 lg:flex-row lg:items-start lg:space-y-0 lg:space-x-8">
              {/* Enhanced Donut Chart */}
              <div
                className="relative flex-shrink-0"
                style={{ width: "180px", height: "180px" }}
              >
                <svg width="180" height="180" className="transform -rotate-90">
                  {/* Background circle */}
                  <circle
                    cx="90"
                    cy="90"
                    r="70"
                    fill="transparent"
                    stroke="#F3F4F6"
                    strokeWidth="16"
                  />
                  {/* VIP segment */}
                  <circle
                    cx="90"
                    cy="90"
                    r="70"
                    fill="transparent"
                    stroke="#F97316"
                    strokeWidth="16"
                    strokeDasharray={`${
                      (analytics.ticketTypeDistribution.vip /
                        analytics.ticketTypeDistribution.total) *
                      439.82
                    } 439.82`}
                    strokeLinecap="round"
                    className="transition-all duration-500"
                  />
                  {/* Regular segment */}
                  <circle
                    cx="90"
                    cy="90"
                    r="70"
                    fill="transparent"
                    stroke="#FB923C"
                    strokeWidth="16"
                    strokeDasharray={`${
                      (analytics.ticketTypeDistribution.regular /
                        analytics.ticketTypeDistribution.total) *
                      439.82
                    } 439.82`}
                    strokeDashoffset={`-${
                      (analytics.ticketTypeDistribution.vip /
                        analytics.ticketTypeDistribution.total) *
                      439.82
                    }`}
                    strokeLinecap="round"
                    className="transition-all duration-500"
                  />
                  {/* Premium segment */}
                  <circle
                    cx="90"
                    cy="90"
                    r="70"
                    fill="transparent"
                    stroke="#FDBA74"
                    strokeWidth="16"
                    strokeDasharray={`${
                      (analytics.ticketTypeDistribution.premium /
                        analytics.ticketTypeDistribution.total) *
                      439.82
                    } 439.82`}
                    strokeDashoffset={`-${
                      ((analytics.ticketTypeDistribution.vip +
                        analytics.ticketTypeDistribution.regular) /
                        analytics.ticketTypeDistribution.total) *
                      439.82
                    }`}
                    strokeLinecap="round"
                    className="transition-all duration-500"
                  />
                </svg>
                {/* Center content */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <p className="text-xs font-medium text-gray-500 mb-1">
                    Total Tickets
                  </p>
                  <p className="text-3xl font-bold text-gray-900">
                    {analytics.ticketTypeDistribution.total.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Statistics */}
              <div className="flex-1 w-full space-y-3">
                {/* VIP */}
                <div className="p-3 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full bg-orange-600" />
                      <span className="text-sm font-semibold text-gray-900">
                        VIP
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-orange-600">
                        {(
                          (analytics.ticketTypeDistribution.vip /
                            analytics.ticketTypeDistribution.total) *
                          100
                        ).toFixed(1)}
                        %
                      </p>
                      <p className="text-xs text-gray-500">
                        {analytics.ticketTypeDistribution.vip} tickets
                      </p>
                    </div>
                  </div>
                </div>

                {/* Regular */}
                <div className="p-3 bg-gradient-to-r from-orange-50/50 to-amber-50/50 rounded-lg border border-orange-100/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full bg-orange-400" />
                      <span className="text-sm font-semibold text-gray-900">
                        Regular
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-orange-500">
                        {(
                          (analytics.ticketTypeDistribution.regular /
                            analytics.ticketTypeDistribution.total) *
                          100
                        ).toFixed(1)}
                        %
                      </p>
                      <p className="text-xs text-gray-500">
                        {analytics.ticketTypeDistribution.regular} tickets
                      </p>
                    </div>
                  </div>
                </div>

                {/* Premium */}
                <div className="p-3 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full bg-amber-400" />
                      <span className="text-sm font-semibold text-gray-900">
                        Premium
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-amber-600">
                        {(
                          (analytics.ticketTypeDistribution.premium /
                            analytics.ticketTypeDistribution.total) *
                          100
                        ).toFixed(1)}
                        %
                      </p>
                      <p className="text-xs text-gray-500">
                        {analytics.ticketTypeDistribution.premium} tickets
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Attendee Demographics */}
          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="mb-1 text-lg font-semibold text-gray-900">
                    Attendee Demographics
                  </h3>
                  <p className="text-xs text-gray-500">
                    Audience insights and breakdown
                  </p>
                </div>
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {/* Gender */}
              <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                <div className="flex items-center justify-center mb-4">
                  <div
                    className="relative"
                    style={{ width: "120px", height: "120px" }}
                  >
                    <svg
                      width="120"
                      height="120"
                      className="transform -rotate-90"
                    >
                      <circle
                        cx="60"
                        cy="60"
                        r="50"
                        fill="transparent"
                        stroke="#E0E7FF"
                        strokeWidth="12"
                      />
                      <circle
                        cx="60"
                        cy="60"
                        r="50"
                        fill="transparent"
                        stroke="#1E40AF"
                        strokeWidth="12"
                        strokeDasharray={`${
                          (analytics.demographics.gender.male / 100) * 314.16
                        } 314.16`}
                        strokeLinecap="round"
                        className="transition-all duration-500"
                      />
                      <circle
                        cx="60"
                        cy="60"
                        r="50"
                        fill="transparent"
                        stroke="#F97316"
                        strokeWidth="12"
                        strokeDasharray={`${
                          (analytics.demographics.gender.female / 100) * 314.16
                        } 314.16`}
                        strokeDashoffset={`-${
                          (analytics.demographics.gender.male / 100) * 314.16
                        }`}
                        strokeLinecap="round"
                        className="transition-all duration-500"
                      />
                    </svg>
                  </div>
                </div>
                <p className="text-center mb-3 text-sm font-semibold text-gray-900">
                  Gender
                </p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-2 bg-white/60 rounded">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full bg-blue-700" />
                      <span className="text-xs font-medium text-gray-700">
                        Male
                      </span>
                    </div>
                    <span className="text-xs font-bold text-gray-900">
                      {analytics.demographics.gender.male}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-white/60 rounded">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full bg-orange-600" />
                      <span className="text-xs font-medium text-gray-700">
                        Female
                      </span>
                    </div>
                    <span className="text-xs font-bold text-gray-900">
                      {analytics.demographics.gender.female}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Age */}
              <div className="p-4 bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg border border-orange-100">
                <div className="flex items-center justify-center mb-4">
                  <div
                    className="relative"
                    style={{ width: "120px", height: "120px" }}
                  >
                    <svg
                      width="120"
                      height="120"
                      className="transform -rotate-90"
                    >
                      <circle
                        cx="60"
                        cy="60"
                        r="50"
                        fill="transparent"
                        stroke="#FED7AA"
                        strokeWidth="12"
                      />
                      {Object.entries(analytics.demographics.age).map(
                        ([label, value], index, arr) => {
                          const colors = [
                            "#F97316",
                            "#FB923C",
                            "#FDBA74",
                            "#FED7AA",
                            "#FFEDD5",
                          ];
                          const total = arr.reduce((sum, [, v]) => sum + v, 0);
                          const percentage = (value / total) * 100;
                          const offset = arr
                            .slice(0, index)
                            .reduce(
                              (sum, [, v]) => sum + (v / total) * 314.16,
                              0
                            );
                          return (
                            <circle
                              key={index}
                              cx="60"
                              cy="60"
                              r="50"
                              fill="transparent"
                              stroke={colors[index % colors.length]}
                              strokeWidth="12"
                              strokeDasharray={`${
                                (percentage / 100) * 314.16
                              } 314.16`}
                              strokeDashoffset={`-${offset}`}
                              strokeLinecap="round"
                              className="transition-all duration-500"
                            />
                          );
                        }
                      )}
                    </svg>
                  </div>
                </div>
                <p className="text-center mb-3 text-sm font-semibold text-gray-900">
                  Age Groups
                </p>
                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                  {Object.entries(analytics.demographics.age).map(
                    ([label, value], index) => {
                      const colors = [
                        "#F97316",
                        "#FB923C",
                        "#FDBA74",
                        "#FED7AA",
                        "#FFEDD5",
                      ];
                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between p-1.5 bg-white/60 rounded text-xs"
                        >
                          <div className="flex items-center space-x-1.5">
                            <div
                              className="w-2.5 h-2.5 rounded-full"
                              style={{
                                backgroundColor: colors[index % colors.length],
                              }}
                            />
                            <span className="font-medium text-gray-700">
                              {label}
                            </span>
                          </div>
                          <span className="font-bold text-gray-900">
                            {value}%
                          </span>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>

              {/* Location */}
              <div className="p-4 bg-gradient-to-br from-red-50 to-rose-50 rounded-lg border border-red-100">
                <div className="flex items-center justify-center mb-4">
                  <div
                    className="relative"
                    style={{ width: "120px", height: "120px" }}
                  >
                    <svg
                      width="120"
                      height="120"
                      className="transform -rotate-90"
                    >
                      <circle
                        cx="60"
                        cy="60"
                        r="50"
                        fill="transparent"
                        stroke="#FEE2E2"
                        strokeWidth="12"
                      />
                      {Object.entries(analytics.demographics.location).map(
                        ([label, value], index, arr) => {
                          const colors = [
                            "#F97316",
                            "#DC2626",
                            "#EF4444",
                            "#F87171",
                            "#FCA5A5",
                          ];
                          const total = arr.reduce((sum, [, v]) => sum + v, 0);
                          const percentage = (value / total) * 100;
                          const offset = arr
                            .slice(0, index)
                            .reduce(
                              (sum, [, v]) => sum + (v / total) * 314.16,
                              0
                            );
                          return (
                            <circle
                              key={index}
                              cx="60"
                              cy="60"
                              r="50"
                              fill="transparent"
                              stroke={colors[index % colors.length]}
                              strokeWidth="12"
                              strokeDasharray={`${
                                (percentage / 100) * 314.16
                              } 314.16`}
                              strokeDashoffset={`-${offset}`}
                              strokeLinecap="round"
                              className="transition-all duration-500"
                            />
                          );
                        }
                      )}
                    </svg>
                  </div>
                </div>
                <p className="text-center mb-3 text-sm font-semibold text-gray-900">
                  Location
                </p>
                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                  {Object.entries(analytics.demographics.location).map(
                    ([label, value], index) => {
                      const colors = [
                        "#F97316",
                        "#DC2626",
                        "#EF4444",
                        "#F87171",
                        "#FCA5A5",
                      ];
                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between p-1.5 bg-white/60 rounded text-xs"
                        >
                          <div className="flex items-center space-x-1.5">
                            <div
                              className="w-2.5 h-2.5 rounded-full"
                              style={{
                                backgroundColor: colors[index % colors.length],
                              }}
                            />
                            <span className="font-medium text-gray-700 truncate">
                              {label}
                            </span>
                          </div>
                          <span className="font-bold text-gray-900">
                            {value}%
                          </span>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Ticket Sales Line Chart Component
const TicketSalesChart = ({ data }) => {
  const maxSales = Math.max(...data.map((d) => d.sales));
  const chartHeight = 180;
  const padding = 15;
  const svgWidth = 100; // percentage

  // Calculate coordinates
  const coordinates = data.map((d, i) => {
    const x = padding + (i * (svgWidth - padding * 2)) / (data.length - 1);
    const y =
      chartHeight -
      padding -
      (d.sales / maxSales) * (chartHeight - padding * 2);
    return { x, y };
  });

  // Create smooth curve using cubic bezier
  const createSmoothPath = (coords) => {
    if (coords.length < 2) return "";

    let path = `M ${coords[0].x}% ${coords[0].y}`;

    for (let i = 0; i < coords.length - 1; i++) {
      const current = coords[i];
      const next = coords[i + 1];
      const prev = coords[i - 1];
      const afterNext = coords[i + 2];

      // Calculate control points for smooth curves
      const cp1x = current.x + (next.x - (prev?.x || current.x)) * 0.3;
      const cp1y = current.y + (next.y - (prev?.y || current.y)) * 0.3;
      const cp2x = next.x - ((afterNext?.x || next.x) - current.x) * 0.3;
      const cp2y = next.y - ((afterNext?.y || next.y) - current.y) * 0.3;

      path += ` C ${cp1x}% ${cp1y}, ${cp2x}% ${cp2y}, ${next.x}% ${next.y}`;
    }

    return path;
  };

  const smoothPath = createSmoothPath(coordinates);

  // Area fill path
  const areaPath =
    smoothPath +
    ` L ${coordinates[coordinates.length - 1].x}% ${chartHeight - padding}` +
    ` L ${coordinates[0].x}% ${chartHeight - padding} Z`;

  return (
    <div
      className="relative bg-gradient-to-b from-orange-50/20 to-transparent rounded-lg pt-2"
      style={{ height: `${chartHeight + 30}px` }}
    >
      <svg width="100%" height={chartHeight} className="overflow-visible">
        {/* Gradient definitions */}
        <defs>
          <linearGradient id="salesGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#F97316" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#F97316" stopOpacity="0.03" />
          </linearGradient>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#F97316" />
            <stop offset="100%" stopColor="#FB923C" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {[0, 1, 2, 3, 4].map((i) => (
          <line
            key={i}
            x1={`${padding}%`}
            y1={padding + (i * (chartHeight - padding * 2)) / 4}
            x2={`${100 - padding}%`}
            y2={padding + (i * (chartHeight - padding * 2)) / 4}
            stroke="#F3F4F6"
            strokeWidth="1"
            strokeDasharray="3 3"
          />
        ))}

        {/* Area fill with smooth curve */}
        <path
          d={areaPath}
          fill="url(#salesGradient)"
          className="transition-all duration-500"
        />

        {/* Smooth line chart with gradient */}
        <path
          d={smoothPath}
          fill="none"
          stroke="url(#lineGradient)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="transition-all duration-500"
        />

        {/* Data points */}
        {coordinates.map((coord, i) => (
          <circle
            key={i}
            cx={`${coord.x}%`}
            cy={coord.y}
            r="4"
            fill="#F97316"
            stroke="white"
            strokeWidth="2"
            className="transition-all duration-300"
          />
        ))}

        {/* X-axis labels */}
        {data.map((d, i) => (
          <text
            key={i}
            x={`${coordinates[i].x}%`}
            y={chartHeight + 12}
            textAnchor="middle"
            className="text-xs font-medium fill-gray-600"
          >
            {d.day}
          </text>
        ))}
      </svg>

      {/* Y-axis labels */}
      <div
        className="absolute left-0 top-2 flex flex-col justify-between"
        style={{ height: `${chartHeight - padding * 2}px`, width: "30px" }}
      >
        {[0, 1, 2, 3, 4].reverse().map((i) => (
          <span key={i} className="text-xs text-gray-500">
            ₵{((i * maxSales) / 4 / 1000).toFixed(1)}k
          </span>
        ))}
      </div>
    </div>
  );
};

export default EventAnalytics;